/**
 * Workspace screenshot utilities.
 *
 * Captures screenshots of the n8n iframe (via the injected mcp-bridge.js)
 * and persists them in localStorage as JPEG data-URLs for use as card
 * thumbnails on the projects page.
 */

const STORAGE_PREFIX = "ws-thumb:";
const REQUEST_TIMEOUT_MS = 3_000;

// ── Public API ──────────────────────────────────────────────────

/**
 * Ask the active iframe's bridge script to capture a screenshot, then
 * save the result to localStorage under `ws-thumb:{workspaceId}`.
 *
 * Resolves once the screenshot is saved (or the timeout expires).
 */
export function requestScreenshot(
  iframe: HTMLIFrameElement,
  workspaceId: string,
): Promise<void> {
  return new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      window.removeEventListener("message", handler);
      resolve();
    }, REQUEST_TIMEOUT_MS);

    function handler(event: MessageEvent) {
      if (event.data?.type !== "screenshot_result") return;
      clearTimeout(timeout);
      window.removeEventListener("message", handler);

      if (event.data.dataUrl) {
        saveScreenshot(workspaceId, event.data.dataUrl);
      }
      resolve();
    }

    window.addEventListener("message", handler);
    iframe.contentWindow?.postMessage({ type: "capture_screenshot" }, "*");
  });
}

/**
 * Retrieve a stored screenshot data-URL for the given workspace, or `null`.
 */
export function getScreenshot(workspaceId: string): string | null {
  try {
    return localStorage.getItem(STORAGE_PREFIX + workspaceId);
  } catch {
    return null;
  }
}

/**
 * Persist a screenshot data-URL in localStorage. If the quota is exceeded
 * the oldest thumbnails are evicted until the write succeeds.
 */
export function saveScreenshot(workspaceId: string, dataUrl: string): void {
  const key = STORAGE_PREFIX + workspaceId;
  try {
    localStorage.setItem(key, dataUrl);
  } catch {
    // Likely QuotaExceededError — evict oldest thumbnails and retry
    evictOldest(3);
    try {
      localStorage.setItem(key, dataUrl);
    } catch {
      // Still failing — give up silently
    }
  }
}

/**
 * Remove the stored screenshot for a workspace.
 */
export function removeScreenshot(workspaceId: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + workspaceId);
  } catch {
    // Ignore
  }
}

// ── Internals ───────────────────────────────────────────────────

/**
 * Remove the `count` oldest `ws-thumb:*` entries from localStorage.
 */
function evictOldest(count: number): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(STORAGE_PREFIX)) keys.push(k);
  }
  // localStorage doesn't track insertion order, so just remove from
  // the front of the key list (effectively arbitrary but deterministic).
  keys.slice(0, count).forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // Ignore
    }
  });
}
